import { BreadcrumbConfig, MonitoringService } from '@kit/monitoring-core';

interface ConsoleSpan {
  name: string;
  op: string;
  startTime: number;
  data?: Record<string, unknown>;
}

export class ConsoleMonitoringService implements MonitoringService {
  identifyUser(data: { id: string }) {
    // Reduce logging to prevent debugger triggers
    console.log(`[Console Monitoring] Identified user`, data);
  }

  captureException(error: Error) {
    // Reduced error logging to prevent debugger behavior
    console.warn(`[Console Monitoring] Exception:`, error.message);
  }

  captureEvent(event: string) {
    // Minimal event logging
    console.log(`[Console Monitoring] Event: ${event}`);
  }

  ready() {
    return Promise.resolve();
  }

  // ============================================
  // Performance Monitoring Methods (Console stubs)
  // ============================================

  startTransaction(
    name: string,
    op: string,
    data?: Record<string, unknown>,
  ): ConsoleSpan {
    console.log(`[Console Monitoring] Transaction Start: ${name} (${op})`, data);

    return { name, op, startTime: Date.now(), data };
  }

  startSpan(
    name: string,
    op: string,
    data?: Record<string, unknown>,
  ): ConsoleSpan {
    console.log(`[Console Monitoring] Span Start: ${name} (${op})`, data);

    return { name, op, startTime: Date.now(), data };
  }

  finishSpan(span: unknown, status?: string): void {
    const s = span as ConsoleSpan;
    const duration = Date.now() - s.startTime;

    console.log(
      `[Console Monitoring] Span End: ${s.name} - ${duration}ms - ${status ?? 'ok'}`,
    );
  }

  finishTransaction(transaction: unknown, status?: string): void {
    const t = transaction as ConsoleSpan;
    const duration = Date.now() - t.startTime;

    console.log(
      `[Console Monitoring] Transaction End: ${t.name} - ${duration}ms - ${status ?? 'ok'}`,
    );
  }

  // ============================================
  // Context & Tagging Methods (Console stubs)
  // ============================================

  addBreadcrumb(breadcrumb: BreadcrumbConfig): void {
    console.log(
      `[Console Monitoring] Breadcrumb [${breadcrumb.category}]: ${breadcrumb.message}`,
      breadcrumb.data,
    );
  }

  setContext(name: string, context: Record<string, unknown>): void {
    console.log(`[Console Monitoring] Context [${name}]:`, context);
  }

  setTag(key: string, value: string): void {
    console.log(`[Console Monitoring] Tag: ${key}=${value}`);
  }

  setTags(tags: Record<string, string>): void {
    console.log(`[Console Monitoring] Tags:`, tags);
  }

  withScope<T>(callback: (scope: unknown) => T): T {
    // For console monitoring, we just execute the callback directly
    return callback({});
  }
}
